import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/lib/auth';

export interface MessageReaction {
  id: string;
  message_id: string;
  user_id: string;
  emoji: string;
  created_at: string;
}

/**
 * Fetches all reactions for a given thread (in one go) and keeps them in sync
 * via realtime. Returns helpers grouped by message id.
 */
export function useThreadReactions(threadId: string | null, messageIds: string[]) {
  const { user } = useAuth();
  const [reactions, setReactions] = useState<MessageReaction[]>([]);

  const idsKey = messageIds.join(',');

  const fetchAll = useCallback(async () => {
    if (!threadId || messageIds.length === 0) {
      setReactions([]);
      return;
    }
    const { data } = await supabase
      .from('client_message_reactions' as any)
      .select('*')
      .in('message_id', messageIds);
    if (data) setReactions(data as unknown as MessageReaction[]);
  }, [threadId, idsKey]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    if (!threadId) return;
    const channel = supabase
      .channel(`message-reactions-${threadId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'client_message_reactions' },
        (payload: any) => {
          const row = (payload.new ?? payload.old) as MessageReaction | undefined;
          if (!row) return;
          if (!messageIds.includes(row.message_id)) return;
          fetchAll();
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [threadId, idsKey, fetchAll]);

  const toggle = useCallback(
    async (messageId: string, emoji: string) => {
      if (!user) return;
      const existing = reactions.find(
        (r) => r.message_id === messageId && r.user_id === user.id && r.emoji === emoji
      );
      if (existing) {
        // Optimistic remove
        setReactions((prev) => prev.filter((r) => r.id !== existing.id));
        await supabase
          .from('client_message_reactions' as any)
          .delete()
          .eq('id', existing.id);
      } else {
        // Optimistic add
        const optimistic: MessageReaction = {
          id: `tmp-${Date.now()}`,
          message_id: messageId,
          user_id: user.id,
          emoji,
          created_at: new Date().toISOString(),
        };
        setReactions((prev) => [...prev, optimistic]);
        const { data, error } = await supabase
          .from('client_message_reactions' as any)
          .insert({ message_id: messageId, user_id: user.id, emoji })
          .select()
          .single();
        if (error) {
          // rollback
          setReactions((prev) => prev.filter((r) => r.id !== optimistic.id));
        } else if (data) {
          setReactions((prev) =>
            prev.map((r) => (r.id === optimistic.id ? (data as unknown as MessageReaction) : r))
          );
        }
      }
    },
    [user, reactions]
  );

  return { reactions, toggle, refetch: fetchAll };
}